import React, { useState } from 'react';
import Hero from '../../components/ui/Hero';
import { useForumTopics } from '../../hooks/useForum';
import { Link } from 'react-router-dom';
import { MessageCircle, Clock, MapPin, Building2, Calendar, Activity } from 'lucide-react';
import Pagination from '../../components/ui/Pagination';
import ForumSort from '../../components/forum/ForumSort';
import type { ForumSortOption, SortDirection } from '../../hooks/useForum';

const ForumPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<ForumSortOption>('recent');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const { topics, totalPages, isLoading } = useForumTopics(currentPage, sortBy, sortDirection);
  const [search, setSearch] = useState('');
  const heroImage = "https://nifmkzddenyukgjcdetc.supabase.co/storage/v1/object/public/animatronics_data/logo_light.webp?t=2024-12-30T20%3A06%3A02.198Z";

  const filteredTopics = topics.filter(topic => 
    topic.title.toLowerCase().includes(search.toLowerCase())
  );

  const handlePageChange = (page: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentPage(page);
  };

  return (
    <div>
      <Hero
        title="Community Forum"
        description="Join the discussion about animatronics worldwide"
        backgroundImage={heroImage}
        compact
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-grow">
            <input
              type="text"
              placeholder="Search topics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg"
            />
          </div>
          <ForumSort
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={setSortBy}
            onDirectionChange={setSortDirection}
          />
        </div>

        {/* Topics List */}
        <div className="space-y-4 mb-8">
          {isLoading ? (
            <div className="text-center py-8 text-content-secondary">Loading topics...</div>
          ) : filteredTopics.length === 0 ? (
            <div className="text-center py-8 text-content-secondary">No topics found</div>
          ) : (
            filteredTopics.map((topic) => (
              <Link
                key={topic.id}
                to={`/community/comments/${topic.id}`}
                className="block p-6 bg-background-secondary rounded-lg border border-gray-800 hover:border-brand/50 transition-all"
              >
                <div className="flex gap-6">
                  {/* Thumbnail */}
                  {topic.animatronic?.media?.[0] && (
                    <div className="hidden sm:block flex-shrink-0 w-32 h-24">
                      <img
                        src={topic.animatronic.media[0].url}
                        alt={topic.animatronic.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  )}
                  
                  <div className="flex-grow">
                    <h3 className="text-lg font-semibold mb-2">{topic.title}</h3>
                    {topic.animatronic && (
                      <>
                        <p className="text-content-secondary mb-2">
                          Discussing: {topic.animatronic.name}
                        </p>
                        <div className="flex flex-wrap gap-4 text-sm text-content-secondary mb-4">
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1.5 text-brand/70" />
                            <span>{topic.animatronic.country}</span>
                          </div>
                          <div className="flex items-center">
                            <Building2 className="w-4 h-4 mr-1.5 text-brand/70" />
                            <span>{topic.animatronic.park}</span>
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1.5 text-brand/70" />
                            <span>{topic.animatronic.year}</span>
                          </div>
                          <div className="flex items-center">
                            <Activity className="w-4 h-4 mr-1.5 text-brand/70" />
                            <span className="capitalize">{topic.animatronic.status}</span>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="flex items-center text-sm text-content-secondary">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      <span>{topic.comments_count} comments</span>
                      <Clock className="w-4 h-4 ml-4 mr-1" />
                      <span>Last activity {new Date(topic.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>

        {/* Pagination */}
        {!search && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

export default ForumPage;